// [목적] /chat 우측 LIVE PREVIEW — 가이드 답변마다 카드가 하나씩 실제 내용으로 바뀌는 미리보기.
// [이유] 2026-09-21 trip-guide: 질문만 이어지면 "이게 뭘 만드는 건지" 알 수 없다. 빈(점선) 카드가 미리
//        깔려 있고 답할 때마다 그 자리가 그림 있는 카드로 채워지면, 남은 단계와 결과물이 동시에 보인다.
// [사이드 이팩트] 없음(순수 표시). 답변 상태는 ChatPage 가 들고 TripGuide 와 공유한다.
// [수정 시 고려사항] 지도(지역)·동반자 카드는 사용자 요청으로 그림 없이 텍스트+칩만 쓴다 — 그림 추가 금지.
//                   색은 packages/ui 토큰만 사용(CLAUDE.md §6-3). 무드/시간대 색은 color.illust 참조.

import type { ReactNode } from 'react';
import { Box, Stack, Typography } from '@mui/material';
import { tokens } from '@gangwoncanvas/ui';

import {
  formatGuideDate,
  formatRangeKm,
  GUIDE_STEPS,
  isSelfDriveLabel,
  RANGE_UNLIMITED,
  isAnswered,
  type GuideAnswers,
  type GuideStepKey,
} from './guideSteps.js';

const c = tokens.color;
const L = 'light' as const;
const INK_HI = c.ink.high[L];
const INK_MID = c.ink.mid[L];
const INK_LOW = c.ink.low[L];
const BORDER = c.line.base[L];
const SOFT_LINE = c.line.soft[L];
const PAPER0 = c.paper[0][L];
const PAPER1 = c.paper[1][L];
const PAPER2 = c.paper[2][L];
const BRAND = c.brand[500][L];
const BRAND_TEXT = c.brand[600][L];
const ACCENT = c.accent[500][L];
const BRONZE = c.badge.bronze.fg[L];
const BRONZE_BG = c.badge.bronze.bgFrom[L];
const GOLD_RING = c.badge.gold.ring[L];
const MONO = 'JetBrains Mono, ui-monospace, monospace';

/** 빈 카드 제목 — 아직 답하지 않은 단계 자리 */
const GHOST_TITLE: Record<GuideStepKey, string> = {
  region: '지도',
  who: '동반자 맞춤',
  care: '이동 조건',
  when: '일정표',
  move: '이동 계획',
  mood: '무드보드',
  budget: '예산 분배',
  detail: '추가 요청',
};

/** 발바닥 — 반려견 관련 조건 전용 */
const PAW_PATH =
  'M8.4 6.6a1.8 2.3 0 1 0 .01 4.6A1.8 2.3 0 0 0 8.4 6.6Zm7.2 0a1.8 2.3 0 1 0 .01 4.6A1.8 2.3 0 0 0 15.6 6.6ZM4.6 12a1.6 2 0 1 0 .01 4A1.6 2 0 0 0 4.6 12Zm14.8 0a1.6 2 0 1 0 .01 4A1.6 2 0 0 0 19.4 12ZM12 12.8c2.3 0 4.2 1.9 4.2 3.8 0 1.7-1.4 2.8-3.1 2.8h-2.2c-1.7 0-3.1-1.1-3.1-2.8 0-1.9 1.9-3.8 4.2-3.8Z';

/**
 * 이동 배려 조건 → 라인 아이콘 path (라벨에 포함된 키워드로 찾는다).
 * [2026-09-21 수정] 1차 아이콘은 24px 에서 형태가 뭉개져(휠체어가 낙서처럼 보임) 전부 다시 그렸다 —
 * 바퀴/머리/기호가 각각 구분되게 크게 잡고, 획이 겹치는 디테일은 뺐다.
 */
const CARE_ICON_PATHS: ReadonlyArray<[string, string]> = [
  // 휠체어 — 머리 + 등받이/팔걸이 + 큰 바퀴 + 발판
  [
    '휠체어',
    'M14.6 2.4a2 2 0 1 1-.02 0zM13.6 7.4 12.6 12.8h4.8l1.8 3.8M13.1 9.6h3.6M12 11.6a5.2 5.2 0 1 1-.02 0z',
  ],
  // 유아차 — 손잡이 + 후드 + 바퀴 2개
  [
    '유아차',
    'M3.4 4.6h2.2l1.9 5.6M7 12.4h10.4M7.6 12.4a5.4 5.4 0 0 1 9.8-3.2M8.6 12.6 9.9 16.6M16.4 12.6 16.6 16.6M9.8 18.6a1.9 1.9 0 1 1-.02 0zM16.6 18.6a1.9 1.9 0 1 1-.02 0z',
  ],
  // 화장실 — 남/녀 픽토그램 + 가운데 구분선
  [
    '화장실',
    'M12 3v18M7.4 3.4a1.5 1.5 0 1 1-.02 0zM5.6 13V9.2a1.8 1.8 0 0 1 3.6 0V13M6.5 13v7.2M8.3 13v7.2M16.6 3.4a1.5 1.5 0 1 1-.02 0zM14.2 13.6 15.9 8.4h1.4l1.7 5.2zM15.6 13.6v6.8M17.6 13.6v6.8',
  ],
  // 장애인 주차 — 안내판 + P
  // 주차 — 'P' + 휠체어(안내판 테두리는 24px 에서 안쪽이 뭉개져 뺐다)
  [
    '주차',
    'M4.6 20V4.6h3.6a3.1 3.1 0 0 1 0 6.2H4.6M18.4 5.6a1.5 1.5 0 1 1-.02 0zM17.8 9v3.4h2.6M16.8 15.4a3.5 3.5 0 1 1-.02 0z',
  ],
  // 엘리베이터 — 문 + 상하 화살표
  ['엘리베이터', 'M5.2 3.4h13.6v17.2H5.2zM12 3.4v17.2M8.6 9.4 7.1 11.6h3zM15.4 11.8 16.9 9.6h-3z'],
  // 경사로 — 완만한 경사 + 이용자
  ['경사로', 'M3 19.6h18M4.4 19.6 17.6 8.2M13.2 5.6a1.8 1.8 0 1 1-.02 0z'],
  // 반려견 동반/대형견 — 발바닥 (매칭 표에 없으면 기타(…)로 떨어지므로 명시한다)
  ['반려견', PAW_PATH],
  ['대형견', PAW_PATH],
  // 리드줄 산책 — 사람 손 → 목줄 → 반려견
  [
    '리드줄',
    'M5.6 4.4a2 2 0 1 1-.02 0zM7 6.6c2.6 2.6 5.8 3.8 8.8 4.2M17.4 12.4a2 2 0 1 1-.02 0zM3 20.4h18',
  ],
];

/**
 * 기타(…) — 직접 입력해서 만든 값처럼 정해진 아이콘이 없는 항목의 폴백.
 * [이유] 2026-09-21 QA: 매칭 실패 시 발바닥이 나와 "계단 없는 곳만" 같은 직접 입력 조건이
 *        반려동물 조건처럼 보였다. 의미가 없는 중립 기호(원 + 점 3개)로 대체한다.
 */
const ETC_PATH = 'M12 3.4a8.6 8.6 0 1 1-.02 0zM8.4 12h.01M12 12h.01M15.6 12h.01';

/** 라벨에 맞는 조건 아이콘 — 매칭 실패(직접 입력 등)는 기타(…) 아이콘 */
function careIconPath(label: string): string {
  const hit = CARE_ICON_PATHS.find(([keyword]) => label.includes(keyword));
  return hit ? hit[1] : ETC_PATH;
}

/** 무드 라벨 → [배경, 전경, 아이콘 path] */
const MOOD_ART: Record<string, [string, string, string]> = {
  바다: [
    c.illust.mood.sea.bg[L],
    c.illust.mood.sea.fg[L],
    'M2 15c2.4-2 4.8-2 7.2 0s4.8 2 7.2 0 4.8-2 7.2 0M2 19.5c2.4-2 4.8-2 7.2 0s4.8 2 7.2 0 4.8-2 7.2 0M17 4.5a3 3 0 1 0 .01 0',
  ],
  카페: [
    c.illust.mood.cafe.bg[L],
    c.illust.mood.cafe.fg[L],
    'M4.5 8h11v4.8a4.8 4.8 0 0 1-4.8 4.8H9.3a4.8 4.8 0 0 1-4.8-4.8Zm11 1.2H17a2.4 2.4 0 0 1 0 4.8h-1.5M7 3.4c0 1.1.9 1.1.9 2.2M11 3.4c0 1.1.9 1.1.9 2.2',
  ],
  맛집: [
    c.illust.mood.food.bg[L],
    c.illust.mood.food.fg[L],
    'M6 3v8a2.5 2.5 0 0 0 5 0V3M8.5 11v10M17 3c-1.8 1.2-2.5 3.4-2.5 5.6 0 1.6.9 2.6 2.5 2.9V21',
  ],
  캠핑: [
    c.illust.mood.camp.bg[L],
    c.illust.mood.camp.fg[L],
    'm12 4 8.5 15.5h-17L12 4Zm0 6.6-4.8 8.9M12 10.6l4.8 8.9M9.4 19.5 12 14.6l2.6 4.9',
  ],
  액티비티: [
    c.illust.mood.activity.bg[L],
    c.illust.mood.activity.fg[L],
    'M13 4.2a1.7 1.7 0 1 0 .01 0M7 20.5l3.2-5 2.4 2.2.8 2.8M10.2 15.5l-.8-4.6 4-2.4 2.6 3.2 3 .8M8 9.2l2.4-1.6',
  ],
  힐링: [
    c.illust.mood.healing.bg[L],
    c.illust.mood.healing.fg[L],
    'M12 20.5c0-5.5 3.5-9 8.5-9.5-.5 5-4 8.5-8.5 9.5Zm0 0C12 15 8.5 11.5 3.5 11c.5 5 4 8.5 8.5 9.5ZM12 20.5V9.5M12 9.5C12 6 13.8 4 16 3.5c0 3-1.6 5.3-4 6Z',
  ],
  포토스팟: [
    c.illust.mood.photo.bg[L],
    c.illust.mood.photo.fg[L],
    'M4 8h3.2l1.6-2.4h6.4L16.8 8H20a1.5 1.5 0 0 1 1.5 1.5v8A1.5 1.5 0 0 1 20 19H4a1.5 1.5 0 0 1-1.5-1.5v-8A1.5 1.5 0 0 1 4 8Zm8 2.6a3.6 3.6 0 1 0 .01 7.2A3.6 3.6 0 0 0 12 10.6Z',
  ],
  축제: [
    c.illust.mood.festival.bg[L],
    c.illust.mood.festival.fg[L],
    'm3 21 4-12 8 8-12 4ZM6 13l5 5M13 11l3-3M16 3v2M21 8h-2M20 3l-1 1M10 3c2 2-1 3 1 5M17 13c2-2 3 1 5-1',
  ],
};

/** 시간대 블록 색 — 일정표 카드 */
const SLOT_COLORS: ReadonlyArray<[string, string]> = [
  [c.illust.slot.morning[L], '오전'],
  [c.illust.slot.noon[L], '점심'],
  [c.illust.slot.afternoon[L], '오후'],
  [c.illust.slot.evening[L], '저녁'],
];

/** 예산 도넛 구성비 — 숙소/식비/이동/체험 */
const BUDGET_SEGMENTS: ReadonlyArray<[string, number, string]> = [
  ['숙소', 34, BRAND],
  ['식비', 26, ACCENT],
  ['이동', 22, GOLD_RING],
  ['체험', 18, PAPER2],
];

function nightsOf(days: string | undefined): number {
  if (days === '당일치기') return 1;
  if (days === '1박 2일') return 2;
  if (days === '2박 3일') return 3;
  return 4;
}

function LineIcon({
  d,
  color,
  size = 22,
}: {
  d: string;
  color: string;
  size?: number;
}): JSX.Element {
  return (
    <Box
      component="svg"
      aria-hidden="true"
      viewBox="0 0 24 24"
      sx={{
        width: size,
        height: size,
        fill: 'none',
        stroke: color,
        strokeWidth: 1.8,
        strokeLinecap: 'round',
        strokeLinejoin: 'round',
      }}
    >
      <path d={d} />
    </Box>
  );
}

/** 카드 공통 껍데기 — 채워진 카드/빈(점선) 카드 */
function PreviewCard({
  title,
  description,
  ghost = false,
  children,
}: {
  title: string;
  description: string;
  ghost?: boolean;
  children?: ReactNode;
}): JSX.Element {
  return (
    <Box
      sx={{
        border: ghost ? `1px dashed ${BORDER}` : `1px solid ${BORDER}`,
        borderRadius: `${tokens.radius.l}px`,
        bgcolor: ghost ? PAPER1 : PAPER0,
        opacity: ghost ? 0.75 : 1,
        p: '16px 18px',
        boxShadow: ghost ? 'none' : `0 1px 2px ${c.overlay.scrimOnBrand[L].replace('.4', '.04')}`,
        animation: 'gwGuideIn .45s cubic-bezier(.2,.8,.25,1) both',
      }}
    >
      <Typography sx={{ fontSize: 13, fontWeight: 800, color: ghost ? INK_LOW : INK_HI }}>
        {title}
      </Typography>
      <Typography sx={{ fontSize: 11.5, color: INK_MID, lineHeight: 1.6, mt: '6px' }}>
        {description}
      </Typography>
      {children}
    </Box>
  );
}

/** 그림/칩이 들어가는 연한 배경 영역 */
function ArtBox({
  height,
  background,
  children,
}: {
  height: number;
  background: string;
  children: ReactNode;
}): JSX.Element {
  return (
    <Box
      aria-hidden="true"
      sx={{
        mt: '12px',
        height,
        borderRadius: `${tokens.radius.m}px`,
        background,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        overflow: 'hidden',
      }}
    >
      {children}
    </Box>
  );
}

function Badge({ label, fg, bg }: { label: string; fg: string; bg: string }): JSX.Element {
  return (
    <Box
      component="span"
      sx={{
        fontSize: 10.5,
        fontWeight: 700,
        px: '9px',
        py: '4px',
        borderRadius: 999,
        bgcolor: bg,
        color: fg,
      }}
    >
      {label}
    </Box>
  );
}

// ─── 단계별 카드 본문 ─────────────────────────────────────────────────────────

function RegionCard({ answers }: { answers: GuideAnswers }): JSX.Element {
  const name = answers.region === '아직 못 정했어요' ? '강원 전역' : answers.region;
  return (
    <PreviewCard title={`${name} 코스`} description="추천 스팟을 찍고 이동 순서대로 이어드려요.">
      <Stack direction="row" spacing="6px" sx={{ mt: '11px', flexWrap: 'wrap', gap: '6px' }}>
        <Badge label={`${name} 출발`} fg={BRAND_TEXT} bg={PAPER2} />
        <Badge label="동선 최적화" fg={BRAND_TEXT} bg={PAPER2} />
        <Badge label="이동 시간 계산" fg={BRAND_TEXT} bg={PAPER2} />
      </Stack>
    </PreviewCard>
  );
}

function WhoCard({ answers }: { answers: GuideAnswers }): JSX.Element {
  const label = answers.count ? `${answers.who} ${answers.count}명` : (answers.who ?? '');
  return (
    <PreviewCard
      title={`동반자 맞춤 · ${label}`}
      description="이동 거리와 체류 시간을 이 인원 기준으로 조정했어요."
    >
      <Stack direction="row" sx={{ mt: '11px', flexWrap: 'wrap', gap: '6px' }}>
        <Badge label={label} fg={BRAND_TEXT} bg={PAPER2} />
        <Badge label="여유로운 동선" fg={BRAND_TEXT} bg={PAPER2} />
        {answers.who === '반려견과' && <Badge label="반려견 동반" fg={BRONZE} bg={BRONZE_BG} />}
      </Stack>
    </PreviewCard>
  );
}

function CareCard({ answers }: { answers: GuideAnswers }): JSX.Element {
  const none = (answers.care?.length ?? 0) === 0;
  const items = none ? ['휠체어', '유아차', '반려견', '주차'] : (answers.care ?? []);
  return (
    <PreviewCard
      title={none ? '이동 조건' : '이동 조건 반영'}
      description={
        none
          ? '특별한 접근성 조건 없이 일반 코스로 구성해요.'
          : '아래 조건을 만족하지 못하는 장소는 코스에서 제외돼요.'
      }
    >
      <ArtBox height={92} background={none ? PAPER1 : BRONZE_BG}>
        <Box sx={{ display: 'flex', gap: '10px', flexWrap: 'wrap', justifyContent: 'center' }}>
          {items.slice(0, 5).map((label) => (
            <Box
              key={label}
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: '5px',
                minWidth: 68,
                maxWidth: 92,
                px: '10px',
                py: '9px',
                textAlign: 'center',
                lineHeight: 1.35,
                wordBreak: 'keep-all',
                borderRadius: `${tokens.radius.m}px`,
                bgcolor: PAPER0,
                border: none ? `1px dashed ${BORDER}` : `1px solid ${BORDER}`,
                opacity: none ? 0.55 : 1,
                fontSize: 10,
                fontWeight: 700,
                color: INK_HI,
              }}
            >
              <LineIcon d={careIconPath(label)} color={none ? INK_LOW : BRONZE} size={24} />
              <span>{label}</span>
            </Box>
          ))}
        </Box>
      </ArtBox>
    </PreviewCard>
  );
}

function MoodCard({ answers }: { answers: GuideAnswers }): JSX.Element {
  const moods = answers.mood ?? [];
  return (
    <PreviewCard title="무드보드" description="선택한 감성에 맞는 장소만 추려요.">
      <ArtBox height={104} background={PAPER1}>
        <Box sx={{ display: 'flex', gap: '9px', flexWrap: 'wrap', justifyContent: 'center' }}>
          {moods.map((mood) => {
            const [bg, fg, path] = MOOD_ART[mood] ?? [PAPER2, INK_MID, ETC_PATH];
            return (
              <Box
                key={mood}
                sx={{
                  width: 84,
                  height: 80,
                  borderRadius: `${tokens.radius.m}px`,
                  bgcolor: bg,
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '7px',
                }}
              >
                <LineIcon d={path} color={fg} size={26} />
                <Box component="b" sx={{ fontSize: 11, fontWeight: 800, color: fg }}>
                  {mood}
                </Box>
              </Box>
            );
          })}
        </Box>
      </ArtBox>
    </PreviewCard>
  );
}

function BudgetCard({ answers }: { answers: GuideAnswers }): JSX.Element {
  const per = answers.budgetPer ?? null;
  const count = answers.count ?? 1;
  const total = per ? per * count : 0;
  const radius = 34;
  const circumference = 2 * Math.PI * radius;
  let offset = 0;

  return (
    <PreviewCard
      title={`예산 분배 · ${per ? `1인 ${per}만원` : '제한 없음'}`}
      description="숙소·식비·이동·체험 비중을 자동으로 나눠요."
    >
      <Box
        sx={{
          mt: '11px',
          p: '10px 12px',
          borderRadius: `${tokens.radius.m}px`,
          bgcolor: PAPER1,
          border: `1px solid ${SOFT_LINE}`,
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          fontSize: 11.5,
          fontWeight: 600,
          color: INK_MID,
        }}
      >
        {per ? (
          <>
            <span>총 {count}명 예상 예산</span>
            <Box component="b" sx={{ color: BRAND_TEXT, fontSize: 13.5, fontWeight: 800 }}>
              약 {total.toLocaleString('ko-KR')}만원
            </Box>
            <Box component="span" sx={{ ml: 'auto', color: INK_LOW }}>
              1인 {per}만원 기준
            </Box>
          </>
        ) : (
          <span>금액 제한 없이 추천 우선으로 코스를 짜드려요</span>
        )}
      </Box>
      <ArtBox height={124} background={PAPER1}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: '18px' }}>
          <Box component="svg" viewBox="0 0 120 120" sx={{ width: 120, height: 120 }}>
            {BUDGET_SEGMENTS.map(([label, percent, color]) => {
              const length = (circumference * percent) / 100;
              const dashOffset = -offset;
              offset += length;
              return (
                <circle
                  key={label}
                  cx="60"
                  cy="60"
                  r={radius}
                  fill="none"
                  stroke={color}
                  strokeWidth="15"
                  strokeDasharray={`${length.toFixed(1)} ${(circumference - length).toFixed(1)}`}
                  strokeDashoffset={dashOffset.toFixed(1)}
                  transform="rotate(-90 60 60)"
                />
              );
            })}
            <text x="60" y="57" textAnchor="middle" fontSize="13" fontWeight="800" fill={INK_HI}>
              {per ? `${total.toLocaleString('ko-KR')}만` : '자유'}
            </text>
            <text x="60" y="73" textAnchor="middle" fontSize="9" fill={INK_MID}>
              {per ? '총 예산' : '예산 기준'}
            </text>
          </Box>
          <Stack spacing="6px">
            {BUDGET_SEGMENTS.map(([label, percent, color]) => (
              <Stack key={label} direction="row" spacing="7px" alignItems="center">
                <Box sx={{ width: 9, height: 9, borderRadius: '3px', bgcolor: color }} />
                <Box component="span" sx={{ fontSize: 11, color: INK_MID, fontWeight: 600 }}>
                  {label}{' '}
                  <Box component="b" sx={{ color: INK_HI, fontWeight: 800 }}>
                    {percent}%
                  </Box>
                </Box>
              </Stack>
            ))}
          </Stack>
        </Box>
      </ArtBox>
    </PreviewCard>
  );
}

/**
 * [목적] 일정표 카드 — 출발일(요일 포함) + Day 블록을 한 장에
 * [이유] [2026-09-21] 가이드에서 날짜·기간을 한 단계로 합쳤으므로 미리보기도 한 장으로 맞춘다.
 *        요일은 이 화면에서 특히 쓸모 있는 정보다(주말이면 붐빈다) — Day 블록 위에 함께 보여준다.
 * [사이드 이팩트] 없음(순수 표시).
 */
function ScheduleCard({ answers }: { answers: GuideAnswers }): JSX.Element {
  const undecided = answers.when === null;
  const dateLabel = undecided ? '날짜 미정' : formatGuideDate(answers.when ?? '');
  const weekend = !undecided && /\(토\)|\(일\)/.test(dateLabel);
  const nights = nightsOf(answers.days);

  return (
    <PreviewCard
      title={`${dateLabel} · ${answers.days ?? ''}`}
      description={
        undecided
          ? `Day 카드가 ${nights}개 생성돼요. 날짜를 정하면 요일에 맞춰 붐비는 곳을 피해드려요.`
          : weekend
            ? `주말 출발이라 인기 장소는 붐빌 수 있어요 — 한산한 시간대로 배치해요.`
            : `평일 출발이라 여유로운 코스로 구성해요.`
      }
    >
      <ArtBox height={40 + nights * 36} background={PAPER1}>
        <Stack spacing="8px" sx={{ width: '100%', px: '14px' }}>
          {!undecided && (
            <Stack direction="row" spacing="6px" sx={{ pl: '52px' }}>
              <Badge
                label={weekend ? '주말 출발' : '평일 출발'}
                fg={weekend ? BRONZE : BRAND_TEXT}
                bg={weekend ? BRONZE_BG : PAPER2}
              />
            </Stack>
          )}
          {Array.from({ length: nights }).map((_, day) => (
            <Stack key={day} direction="row" spacing="10px" alignItems="center">
              <Box
                component="span"
                sx={{ fontSize: 9.5, fontWeight: 800, color: INK_MID, width: 42, fontFamily: MONO }}
              >
                DAY {day + 1}
              </Box>
              <Box sx={{ display: 'flex', gap: '6px', flex: 1 }}>
                {Array.from({ length: 3 + (day % 2) }).map((__, block) => {
                  const [color, label] = SLOT_COLORS[block % SLOT_COLORS.length] ?? SLOT_COLORS[0]!;
                  return (
                    <Box
                      key={block}
                      sx={{
                        flex: 1 + (block % 2) * 0.6,
                        display: 'flex',
                        alignItems: 'center',
                        gap: '5px',
                        borderRadius: '7px',
                        px: '8px',
                        py: '5px',
                        fontSize: 9.5,
                        fontWeight: 700,
                        color: INK_MID,
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        bgcolor: PAPER0,
                        borderLeft: `3px solid ${color}`,
                      }}
                    >
                      {label}
                    </Box>
                  );
                })}
              </Box>
            </Stack>
          ))}
        </Stack>
      </ArtBox>
    </PreviewCard>
  );
}

/**
 * [목적] 이동 계획 카드 — 이동수단 + 반경을 한 장에
 * [이유] [2026-09-21] 가이드에서 두 질문을 한 단계로 합쳤으므로 미리보기도 한 장으로 맞춘다.
 *        둘은 함께 봐야 의미가 있다 — "시외버스 + 30km"는 환승 각오, "자가용 + 30km"는 무난이다.
 * [사이드 이팩트] 없음(순수 표시).
 */
function MoveCard({ answers }: { answers: GuideAnswers }): JSX.Element {
  const selfDrive = isSelfDriveLabel(answers.transport);
  const km = answers.rangeKm;
  const unlimited = km === RANGE_UNLIMITED;
  const label = formatRangeKm(km);
  // 동심원 비율 — 1~60km 를 0.3~1.0 으로 매핑(작을수록 원이 작다)
  const ratio = unlimited || km === undefined ? 1 : 0.3 + (Math.min(km, 60) / 60) * 0.7;

  return (
    <PreviewCard
      title={`이동 계획 · ${answers.transport ?? ''}`}
      description={
        selfDrive
          ? '자차 이동이라 대중교통이 닿지 않는 곳도 넣을 수 있어요.'
          : '대중교통 이동이라 역·터미널에서 닿는 곳 위주로, 환승 시간까지 넣어 짜요.'
      }
    >
      <ArtBox height={92} background={PAPER1}>
        <Stack direction="row" alignItems="center" spacing={2}>
          {/* 반경 동심원 — km 숫자만으로는 감이 오지 않는다 */}
          <Box
            aria-hidden
            sx={{
              position: 'relative',
              width: 64,
              height: 64,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Box
              sx={{
                position: 'absolute',
                width: 64,
                height: 64,
                borderRadius: '50%',
                border: `1px dashed ${BRAND_TEXT}`,
                opacity: 0.35,
              }}
            />
            <Box
              sx={{
                position: 'absolute',
                width: 64 * ratio,
                height: 64 * ratio,
                borderRadius: '50%',
                bgcolor: PAPER2,
                border: `1.5px solid ${BRAND_TEXT}`,
              }}
            />
            <Typography sx={{ fontSize: 11, fontWeight: 800, color: INK_HI, zIndex: 1 }}>
              {unlimited ? '∞' : `${km}km`}
            </Typography>
          </Box>
          <Stack spacing={0.6} alignItems="flex-start">
            <Badge
              label={selfDrive ? '동선 자유' : '환승 시간 반영'}
              fg={selfDrive ? BRAND_TEXT : BRONZE}
              bg={selfDrive ? PAPER2 : BRONZE_BG}
            />
            <Typography sx={{ fontSize: 11.5, color: INK_MID }}>
              {unlimited ? '거리 제한 없음' : `거점에서 ${label}`}
            </Typography>
          </Stack>
        </Stack>
      </ArtBox>
    </PreviewCard>
  );
}

/**
 * [목적] 추가 요청 카드 — 마지막 단계에서 적은 자유 요청을 그대로 보여준다
 * [이유] [2026-09-21] 사용자가 적은 문장이 실제로 반영된다는 걸 눈으로 확인시켜 준다.
 *        '없어요'로 넘긴 경우에도 카드를 채워, 단계를 건너뛴 게 아니라 답한 것임을 분명히 한다.
 * [사이드 이팩트] 없음(순수 표시). 사용자 입력을 그대로 렌더하므로 별도 가공/요약을 하지 않는다.
 */
function DetailCard({ answers }: { answers: GuideAnswers }): JSX.Element {
  const none = answers.detail === null || !answers.detail;
  return (
    <PreviewCard
      title="추가 요청"
      description={
        none
          ? '특별한 요청 없이 추천 코스 그대로 구성해요.'
          : '아래 요청을 코스에 반영해서 만들어요.'
      }
    >
      <ArtBox height={72} background={none ? PAPER1 : PAPER2}>
        <Typography
          sx={{
            fontSize: 12,
            fontWeight: none ? 600 : 700,
            color: none ? INK_LOW : INK_HI,
            textAlign: 'center',
            px: '18px',
            lineHeight: 1.6,
          }}
        >
          {none ? '추가 요청 없음' : `“${answers.detail}”`}
        </Typography>
      </ArtBox>
    </PreviewCard>
  );
}

const CARD_BY_KEY: Record<GuideStepKey, (props: { answers: GuideAnswers }) => JSX.Element> = {
  region: RegionCard,
  who: WhoCard,
  care: CareCard,
  when: ScheduleCard,
  move: MoveCard,
  mood: MoodCard,
  budget: BudgetCard,
  detail: DetailCard,
};

export interface GuidePreviewProps {
  answers: GuideAnswers;
  /** 모든 단계를 끝냈는지 — 완료 카드 노출 */
  completed?: boolean;
}

/**
 * [목적] 가이드 진행 상황을 카드 스택으로 보여주는 우측 패널
 * [이유] 답변 → 결과물 대응을 즉시 보여줘야 단계 진행 동기가 생긴다.
 * [사이드 이팩트] 없음(순수 표시).
 */
export function GuidePreview({ answers, completed = false }: GuidePreviewProps): JSX.Element {
  const answeredCount = GUIDE_STEPS.filter((step) => isAnswered(step.key, answers)).length;

  return (
    <Stack spacing="12px">
      <Stack direction="row" alignItems="center" justifyContent="space-between">
        <Typography sx={{ fontSize: 11.5, color: INK_LOW, fontWeight: 600 }}>
          답할 때마다 아래 카드가 하나씩 채워져요.
        </Typography>
        <Typography sx={{ fontSize: 11, fontWeight: 700, color: BRAND_TEXT, fontFamily: MONO }}>
          {answeredCount}/{GUIDE_STEPS.length} 완성
        </Typography>
      </Stack>

      {GUIDE_STEPS.map((step) => {
        if (!isAnswered(step.key, answers)) {
          return (
            <PreviewCard
              key={step.key}
              ghost
              title={GHOST_TITLE[step.key]}
              description="선택하면 여기에 나타나요."
            >
              <Box
                aria-hidden="true"
                sx={{
                  mt: '10px',
                  height: 12,
                  width: '70%',
                  borderRadius: '7px',
                  bgcolor: PAPER2,
                }}
              />
            </PreviewCard>
          );
        }
        const Card = CARD_BY_KEY[step.key];
        return <Card key={step.key} answers={answers} />;
      })}

      {completed && (
        <PreviewCard
          title="완성 준비 끝!"
          description="왼쪽 버튼을 누르면 이 구성으로 폴리오를 그려드려요."
        />
      )}
    </Stack>
  );
}
